/**
 * Drop `params` from the address bar AND from Next's own record of this history entry.
 *
 * Every param-stripping site goes through here (the package picker, the home's `?msid=` arrival),
 * and both halves are load-bearing:
 *
 *  - **The state object must survive.** It carries Next's per-entry `key`, and `lib/drawer-nav.ts`
 *    hangs the app's whole BACK/MENU decision on that key — an entry with no key reads as having
 *    nothing behind it, so back sends the buyer home instead of to the screen that opened the
 *    picker. Handing `replaceState` a null state wipes it silently.
 *  - **`state.url` / `state.as` must be rewritten too.** Next's popstate handler replays them
 *    verbatim when the entry is reached again by back/forward (`router.js`'s
 *    `const { url, as, options, key } = state`), so leaving them alone puts the params back in the
 *    address bar and in `router.query` — replaying the very checkout, top-up and attribution
 *    hand-offs these callers strip to make one-shot. They are rewritten SEPARATELY because they
 *    are different paths: `as` carries the active mount prefix and `url` is the bare route.
 */
export function stripParams(params: readonly string[]): void {
  rewriteParams(params, {});
}

/**
 * {@link stripParams}, also SETTING `set` on the same entry — under the same two rules, so a value written
 * here is what Back/Forward and a reload find.
 */
export function rewriteParams(remove: readonly string[], set: Readonly<Record<string, string>>): void {
  const without = (raw: string) => {
    const u = new URL(raw, window.location.origin);
    remove.forEach((param) => u.searchParams.delete(param));
    Object.entries(set).forEach(([param, value]) => u.searchParams.set(param, value));
    return `${u.pathname}${u.search}${u.hash}`;
  };
  const state = window.history.state as { url?: unknown; as?: unknown } | null;
  const next =
    state && typeof state === 'object'
      ? {
          ...state,
          ...(typeof state.url === 'string' ? { url: without(state.url) } : {}),
          ...(typeof state.as === 'string' ? { as: without(state.as) } : {}),
        }
      : state;
  window.history.replaceState(next, '', without(window.location.href));
}
